// Story 8.12 (ADO-2021, D-6) — the hooks behind the on-order chip.
//
// `useOnOrder(skus)` drives population: a surface calls it with the SKUs it renders (the
// `useAvailability` shape) and the store reads the ones it has not answered
// yet, or answered too long ago, for the signed-in user. `useOnOrderFor(sku)` is what a chip
// reads. With no signed-in user nothing is read, and a sign-out drops the cache.

import { useEffect } from 'react';
import type { OnOrderProduct } from '../api/on-order.client';
import { useSessionStore } from '../auth/useSessionStore';
import { useOnOrderStore } from './useOnOrderStore';

export function useOnOrder(skuCodes: readonly string[]): void {
  // Key the effect on the VALUE of the list: callers rebuild the array every render.
  const skuKey = [...skuCodes].sort().join('\u0000');
  const userKey = useSessionStore((s) => s.identity?.sub ?? null);

  useEffect(() => {
    if (userKey === null) {
      if (useOnOrderStore.getState().userKey !== null) useOnOrderStore.getState().reset();
      return;
    }
    if (skuKey === '') return;
    // `skuCodes` is recomputed in the same render that produced `skuKey`, so it matches.
    void useOnOrderStore.getState().loadForSkus(userKey, skuCodes);
  }, [skuKey, userKey]);
}

/** The open orders holding `skuCode`, or `undefined` when it is not on order or not answered. */
export function useOnOrderFor(skuCode: string): OnOrderProduct | undefined {
  return useOnOrderStore((s) => {
    const answer = s.bySku[skuCode];
    return answer === undefined || answer === 'none' ? undefined : answer;
  });
}
